/**
 * SeeMe Sheet Section Nav
 * Section navigation for long detail surfaces (sheet bodies + full-page views).
 *
 * Two markup variants, both supported — and either may render as chips OR tabs.
 *
 * 1. Button + data-sheet-nav-target / data-sheet-section (sheet-scoped):
 *
 *    <div class="tabs" data-sheet-nav>
 *        <button class="tabs-button" data-sheet-nav-target="lead">Lead</button>
 *        <button class="tabs-button" data-sheet-nav-target="pricing">Pricing</button>
 *    </div>
 *    <div data-sheet-section="lead">...</div>
 *    <div data-sheet-section="pricing">...</div>
 *
 * 2. Anchor links (works inside a sheet OR on a full page):
 *
 *    <div class="tabs" data-sheet-nav>
 *        <a class="tabs-button" href="#owner-card">Owner</a>
 *        <a class="tabs-button" href="#property-card">Property</a>
 *    </div>
 *    <div id="owner-card">...</div>
 *    <div id="property-card">...</div>
 *
 * A single jump link outside any strip (a related-row count chip such as
 * `Assignments · 3`) also works inside a sheet: same `data-sheet-nav-target`
 * or `href="#id"`, scrolled into view with no active state.
 *
 * Behavior:
 *  - Click a nav item → smooth-scroll its section into view (+ highlight it now).
 *  - Scroll-spy → as sections scroll past the top of the viewport, the matching
 *    nav item highlights automatically.
 *
 * Active state is component-neutral: both `chip-active` (chip markup) and
 * `tabs-button--active` (tab markup) are toggled, so the same JS drives chip
 * strips and tab strips without any per-call configuration. Chips ignore the
 * tab class and vice-versa, so existing chip consumers are unaffected.
 */

(function () {
    var ACTIVE_CLASSES = ['chip-active', 'tabs-button--active'];

    // Per-strip "click lock". After a tab click we force the clicked tab
    // active and have scroll-spy yield to it, because the programmatic
    // smooth-scroll fires the IntersectionObserver mid-animation (and a target
    // near the bottom may never reach the activation band) — without the lock
    // the highlight snaps back to whatever section is topmost, so the click
    // appears to do nothing until you click a second time. The lock releases
    // the moment the user scrolls of their own accord (see below).
    var locks = new WeakMap();   // strip -> locked nav item

    // Resolve the section element a nav item points at. Supports both the
    // data-sheet-nav-target (sheet-scoped) and href="#id" (anchor) wirings.
    function targetFor(item) {
        var key = item.getAttribute('data-sheet-nav-target');
        if (key) {
            var scope = item.closest('.sheet') || document;
            return scope.querySelector('[data-sheet-section="' + key + '"]');
        }
        var href = item.getAttribute('href');
        if (href && href.charAt(0) === '#') {
            return document.getElementById(href.slice(1));
        }
        return null;
    }

    function navItems(strip) {
        return Array.prototype.slice.call(
            strip.querySelectorAll('[data-sheet-nav-target], a[href^="#"]')
        );
    }

    // Highlight exactly one item in the strip (clears the others).
    function setActive(strip, activeItem) {
        navItems(strip).forEach(function (item) {
            var on = item === activeItem;
            ACTIVE_CLASSES.forEach(function (cls) {
                item.classList.toggle(cls, on);
            });
        });
    }

    // ── Click: smooth-scroll to the section, highlight it immediately ──────
    document.addEventListener('click', function (e) {
        var item = e.target.closest(
            '[data-sheet-nav-target], .sheet a[href^="#"]'
        );
        if (!item) return;
        var strip = item.closest('[data-sheet-nav]');
        var target = targetFor(item);

        // A jump link outside a strip (a related-row count chip): the sheet
        // body is its own scroll container, so a native #id jump never moves
        // it. Scroll here; no active state. Plain href="#" links resolve to no
        // target and fall through to their own handlers.
        if (!strip) {
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        if (item.tagName === 'A') e.preventDefault();
        locks.set(strip, item);          // click wins until the user scrolls
        setActive(strip, item);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // A genuine user scroll gesture (not the programmatic click-scroll above)
    // releases every lock so scroll-spy resumes following the scroll position.
    ['wheel', 'touchmove', 'keydown'].forEach(function (ev) {
        window.addEventListener(ev, function () { locks = new WeakMap(); }, { passive: true });
    });

    // ── Scroll-spy: highlight the section currently nearest the top ────────
    // One IntersectionObserver per strip, scoped to that strip's sections.
    // Viewport-based intersection works for both window scroll (full-page
    // strips) and sheet-body scroll (sections still move through the viewport).
    function initSpy(strip) {
        if (strip.dataset.spyInit) return;

        var itemForTarget = new Map();
        navItems(strip).forEach(function (item) {
            var t = targetFor(item);
            if (t) itemForTarget.set(t, item);
        });
        if (!itemForTarget.size) return;   // sections not in DOM yet — retry on next scan
        strip.dataset.spyInit = '1';

        // Default-highlight the first item so the strip reads as a tab bar at
        // rest (one tab active); scroll-spy then takes over as sections scroll.
        var items = navItems(strip);
        if (items.length) setActive(strip, items[0]);

        var visible = new Set();
        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) visible.add(entry.target);
                else visible.delete(entry.target);
            });
            // A pending click owns the highlight until the user scrolls — keep
            // the visible set fresh above, but don't let spy override the click.
            var locked = locks.get(strip);
            if (locked) { setActive(strip, locked); return; }
            // Among the sections crossing the activation band, pick the
            // topmost (smallest top — i.e. the one we're furthest into).
            var best = null, bestTop = Infinity;
            visible.forEach(function (t) {
                var top = t.getBoundingClientRect().top;
                if (top < bestTop) { bestTop = top; best = t; }
            });
            if (best) setActive(strip, itemForTarget.get(best));
        }, { rootMargin: '0px 0px -70% 0px', threshold: 0 });

        itemForTarget.forEach(function (item, target) { observer.observe(target); });
    }

    function scan(root) {
        (root || document).querySelectorAll('[data-sheet-nav]').forEach(initSpy);
    }

    // Initial pass for server-rendered strips (full-page mobile views).
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { scan(); });
    } else {
        scan();
    }

    // Sheets / HTMX inject nav strips after load — watch the DOM and init any
    // strip once it (and its sections) are present. initSpy is idempotent.
    function watch() {
        var mo = new MutationObserver(function (mutations) {
            for (var i = 0; i < mutations.length; i++) {
                if (mutations[i].addedNodes.length) { scan(); return; }
            }
        });
        mo.observe(document.body, { childList: true, subtree: true });
    }
    if (document.body) watch();
    else document.addEventListener('DOMContentLoaded', watch);
})();
